import React, { Component } from 'react';
import './metadata.css';
import logo from './logo.png';
import { Button } from '@fluentui/react-components';
import { 
  Navigation24Regular,
  DatabaseFilled,
  OrganizationFilled,
  SearchFilled,
  FlashFilled,
  ShieldCheckmarkFilled,
  DataTrendingFilled,
  DocumentDataFilled,
  CloudSyncFilled
} from '@fluentui/react-icons';

interface MetadataProps {}

interface MetadataState {
  isMenuOpen: boolean;
  isProductsMenuOpen: boolean;
}

class Metadata extends Component<MetadataProps, MetadataState> {
  constructor(props: MetadataProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      isProductsMenuOpen: false
    };
  }

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  render() {
    return (
      <div className="metadata-page">
        {/* Top Navigation */}
        <nav className="metadata-top-nav">
          <div className="metadata-nav-container">
            <div className="metadata-logo" onClick={() => window.location.href = '/'}>
              <img loading="lazy" decoding="async" src={logo} alt="Meta-365 Logo" />
            </div>
            <Button 
              className="metadata-menu-toggle" 
              onClick={this.toggleMenu}
              appearance="subtle"
              icon={<Navigation24Regular />}
            />
            <ul className={`metadata-nav-menu ${this.state.isMenuOpen ? 'active' : ''}`}>
              <li onClick={() => window.location.href = '/#solutions'}>Solutions</li>
              <li 
                className="metadata-nav-menu-item-with-submenu"
                onMouseEnter={this.toggleProductsMenu}
                onMouseLeave={this.closeProductsMenu}
                onClick={(e) => {
                  if (window.innerWidth <= 768) {
                    e.stopPropagation();
                    this.toggleProductsMenu();
                  }
                }}
              >
                Products
                {this.state.isProductsMenuOpen && (
                  <ul className="metadata-submenu">
                    <li onClick={() => window.location.href = '/solutions/pentities'}>
                      <strong>pEntities</strong>
                      <span>Entity Management</span>
                    </li>
                    <li onClick={() => window.location.href = '/solutions/pshareemail'}>
                      <strong>pShare Email</strong>
                      <span>Email Collaboration</span>
                    </li>
                    <li onClick={() => window.location.href = '/solutions/docsearch'}>
                      <strong>DocSearch</strong>
                      <span>Document Discovery</span>
                    </li>
                    <li onClick={() => window.location.href = '/solutions/searchassistant'}>
                      <strong>Search Assistant</strong>
                      <span>AI-Powered Search</span>
                    </li>
                  </ul>
                )}
              </li>
              {/* <li onClick={() => window.location.href = '/#customers'}>Customers</li> */}
              <li onClick={() => window.location.href = '/about'}>About</li>
              <li onClick={() => window.location.href = '/#contact'}>Contact</li>
            </ul>
          </div>
        </nav>

        {/* Introduction Section */}
        <section className="metadata-hero-section">
          <div className="metadata-hero-content">
            <div className="metadata-hero-text">
              <h1 className="metadata-hero-title">
                Metadata in Microsoft 365
              </h1>
              <p className="metadata-hero-subtitle">
                Metadata provides structured information about documents and content within Microsoft 365 environments. 
                By adding attributes such as project names, departments, document types, and business context to files, 
                organizations can improve content discovery, enable automated workflows, ensure compliance, and transform 
                unstructured storage into an intelligent information management system.
              </p>
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="metadata-challenge-section">
          <div className="metadata-container">
            <h2>Overview</h2>
            <p className="metadata-challenge-text">
              In Microsoft 365, metadata refers to data that describes other data. Common examples include author names, 
              creation dates, document categories, project codes, and custom business attributes. When properly implemented, 
              metadata enables organizations to locate information quickly, automate document-centric processes, and maintain 
              consistent information governance across SharePoint, OneDrive, and Teams.
            </p>
          </div>
        </section>

        {/* Core Concepts Section */}
        <section className="metadata-intelligence-section">
          <div className="metadata-container">
            <h2 className="metadata-section-title">Core Concepts</h2>
            <p className="metadata-section-subtitle">
              Metadata adds structure and meaning to documents through attributes such as project names, clients, 
              departments, statuses, and retention categories. These attributes enable search functionality, 
              content organization, and integration with business processes.
            </p>
            
            <div className="metadata-intelligence-grid">
              <div className="metadata-intelligence-card">
                <div className="metadata-intelligence-icon-wrapper discover">
                  <SearchFilled className="metadata-intelligence-icon" />
                </div>
                <h3>Discovery & Search</h3>
                <p>
                  Metadata enables precise document discovery through structured search fields. Users can locate 
                  content by project, client, status, or custom business attributes without relying on file names 
                  or folder structures.
                </p>
              </div>

              <div className="metadata-intelligence-card">
                <div className="metadata-intelligence-icon-wrapper organize">
                  <OrganizationFilled className="metadata-intelligence-icon" />
                </div>
                <h3>Automatic Organization</h3>
                <p>
                  Metadata-based rules enable automatic content organization. Documents can be routed to designated 
                  locations, have security policies applied, and maintain consistent structure through metadata-driven 
                  information architecture.
                </p>
              </div>

              <div className="metadata-intelligence-card">
                <div className="metadata-intelligence-icon-wrapper align">
                  <DatabaseFilled className="metadata-intelligence-icon" />
                </div>
                <h3>Process Integration</h3>
                <p>
                  Business context applied through metadata allows documents to integrate with workflows. 
                  Content can participate in structured processes from creation to archival with full traceability 
                  through metadata attributes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Automation Section */}
        <section className="metadata-automation-section">
          <div className="metadata-container">
            <h2 className="metadata-section-title">Metadata-Driven Automation</h2>
            <p className="metadata-section-subtitle">
              Metadata serves as a foundation for automated workflows in Microsoft 365. Metadata values can trigger 
              processes, route content, and activate policies without manual intervention.
            </p>
            
            <div className="metadata-automation-grid">
              <div className="metadata-automation-card">
                <div className="metadata-automation-number">01</div>
                <h3>Content Routing</h3>
                <p>
                  Metadata values can trigger automatic document routing to appropriate teams or approval workflows. 
                  For example, when a contract status field changes to "Pending Approval," associated workflows 
                  activate automatically.
                </p>
              </div>

              <div className="metadata-automation-card">
                <div className="metadata-automation-number">02</div>
                <h3>State-Based Triggers</h3>
                <p>
                  Power Automate workflows can monitor metadata field changes. Status transitions such as "Draft" 
                  to "Review" trigger automated processes including notifications, task assignments, and deadline tracking.
                </p>
              </div>

              <div className="metadata-automation-card">
                <div className="metadata-automation-number">03</div>
                <h3>Policy Application</h3>
                <p>
                  Metadata enables automatic application of sensitivity labels, retention rules, and security policies. 
                  Documents tagged with specific classifications receive appropriate controls such as encryption and 
                  access restrictions.
                </p>
              </div>

              <div className="metadata-automation-card">
                <div className="metadata-automation-number">04</div>
                <h3>Business Intelligence</h3>
                <p>
                  Metadata serves as a data source for Power BI dashboards and reporting. Organizations can track 
                  metrics such as contract statuses, project progress, and document volumes using metadata as the 
                  underlying data structure.
                </p>
              </div>
            </div>


          </div>
        </section>

        {/* Platform Integration Section */}
        <section className="metadata-productivity-section">
          <div className="metadata-container">
            <h2 className="metadata-section-title">Integration Across Microsoft 365</h2>
            <p className="metadata-section-subtitle">
              Metadata functions consistently across SharePoint, OneDrive, and Teams, enabling unified information 
              management across Microsoft 365 applications.
            </p>
            
            <div className="metadata-productivity-grid">
              <div className="metadata-productivity-card">
                <div className="metadata-productivity-icon-wrapper sharepoint">
                  <DocumentDataFilled className="metadata-productivity-icon" />
                </div>
                <h3>SharePoint Implementation</h3>
                <p>
                  In SharePoint, metadata enables dynamic views, content organization, and workflow automation. 
                  Documents can be organized, filtered, and processed based on metadata values, creating structured 
                  information architecture.
                </p>
                <ul className="metadata-productivity-list">
                  <li>Metadata-driven views and filtering</li>
                  <li>Automated content organization</li>
                  <li>Consistent taxonomy implementation</li>
                </ul>
              </div>

              <div className="metadata-productivity-card">
                <div className="metadata-productivity-icon-wrapper onedrive">
                  <CloudSyncFilled className="metadata-productivity-icon" />
                </div>
                <h3>OneDrive Integration</h3>
                <p>
                  OneDrive supports metadata through integration with SharePoint libraries. Personal files can inherit 
                  organizational metadata schemas, allowing individual content to follow enterprise classification standards.
                </p>
                <ul className="metadata-productivity-list">
                  <li>Integration with enterprise taxonomy</li>
                  <li>Metadata inheritance from libraries</li>
                  <li>Consistent classification schemas</li>
                </ul>
              </div>

              <div className="metadata-productivity-card">
                <div className="metadata-productivity-icon-wrapper teams">
                  <OrganizationFilled className="metadata-productivity-icon" />
                </div>
                <h3>Teams Integration</h3>
                <p>
                  Microsoft Teams channels store files in SharePoint, maintaining metadata associations. Documents 
                  shared in Teams retain their metadata properties, enabling consistent classification and retrieval 
                  within the collaboration context.
                </p>
                <ul className="metadata-productivity-list">
                  <li>SharePoint-backed file storage</li>
                  <li>Metadata preservation in channels</li>
                  <li>Consistent classification in collaboration</li>
                </ul>
              </div>
            </div>


          </div>
        </section>

        {/* Governance Section */}
        <section className="metadata-scale-section">
          <div className="metadata-container">
            <h2 className="metadata-section-title">Governance and Compliance</h2>
            <p className="metadata-section-subtitle">
              Metadata supports information governance, security, and compliance requirements within Microsoft 365 
              through integration with Microsoft Purview and related services.
            </p>
            
            <div className="metadata-scale-grid">
              <div className="metadata-scale-card">
                <ShieldCheckmarkFilled className="metadata-scale-icon compliance" />
                <h3>Retention Management</h3>
                <p>
                  Metadata enables automated retention and classification policies. Microsoft Purview uses metadata 
                  to apply retention schedules, sensitivity labels, and maintain audit trails for regulatory compliance.
                </p>
              </div>

              <div className="metadata-scale-card">
                <ShieldCheckmarkFilled className="metadata-scale-icon security" />
                <h3>Security Policies</h3>
                <p>
                  Metadata classification can trigger security controls. Data Loss Prevention (DLP) policies, access 
                  controls, and encryption can activate based on metadata values, automating information protection 
                  processes.
                </p>
              </div>

              <div className="metadata-scale-card">
                <DataTrendingFilled className="metadata-scale-icon lifecycle" />
                <h3>Content Lifecycle</h3>
                <p>
                  Metadata supports content lifecycle management from creation to disposal. Documents progress through 
                  stages (active, archived, disposed) based on metadata-driven rules integrated with Microsoft 365 
                  retention policies.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Applications Section */}
        <section className="metadata-benefits-section">
          <div className="metadata-container">
            <h2>Common Applications</h2>
            <p className="metadata-section-subtitle">
              Organizations implement metadata in Microsoft 365 for various operational purposes.
            </p>
            
            <div className="metadata-benefits-grid">
              <div className="metadata-benefit-card">
                <div className="metadata-benefit-icon-wrapper search">
                  <SearchFilled className="metadata-benefit-icon" />
                </div>
                <h3>Search Optimization</h3>
                <p>
                  Metadata improves search precision and reduces time spent locating information. Structured attributes 
                  enable specific queries that return relevant results based on business context rather than just keywords.
                </p>
              </div>

              <div className="metadata-benefit-card">
                <div className="metadata-benefit-icon-wrapper automation">
                  <FlashFilled className="metadata-benefit-icon" />
                </div>
                <h3>Process Automation</h3>
                <p>
                  Metadata enables workflow automation by providing structured data for process triggers. Document routing, 
                  approvals, and state transitions can execute automatically based on metadata values.
                </p>
              </div>

              <div className="metadata-benefit-card">
                <div className="metadata-benefit-icon-wrapper governance">
                  <ShieldCheckmarkFilled className="metadata-benefit-icon" />
                </div>
                <h3>Compliance Enforcement</h3>
                <p>
                  Metadata supports compliance through audit trails, retention policy enforcement, and security control 
                  application. Classification metadata enables systematic information governance.
                </p>
              </div>

              <div className="metadata-benefit-card">
                <div className="metadata-benefit-icon-wrapper collaboration">
                  <OrganizationFilled className="metadata-benefit-icon" />
                </div>
                <h3>Information Consistency</h3>
                <p>
                  Shared metadata standards reduce content duplication and improve consistency. Common classification 
                  schemes enable departments to locate and utilize information created by other teams.
                </p>
              </div>

              <div className="metadata-benefit-card">
                <div className="metadata-benefit-icon-wrapper insights">
                  <DataTrendingFilled className="metadata-benefit-icon" />
                </div>
                <h3>Analytics Integration</h3>
                <p>
                  Structured metadata serves as a data source for analytics and business intelligence. Organizations 
                  can generate reports, track metrics, and analyze content patterns using metadata as the underlying 
                  data structure.
                </p>
              </div>

              <div className="metadata-benefit-card">
                <div className="metadata-benefit-icon-wrapper efficiency">
                  <DatabaseFilled className="metadata-benefit-icon" />
                </div>
                <h3>Scalable Architecture</h3>
                <p>
                  Metadata-based information architecture supports growth by providing consistent classification at scale. 
                  Organizations can manage expanding content repositories without proportional increases in manual management.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Implementation Section */}
        <section className="metadata-pentities-section">
          <div className="metadata-container">
            <h2 className="metadata-section-title">Implementation Approaches</h2>
            <p className="metadata-section-subtitle">
              Various tools and methodologies exist for implementing metadata management in Microsoft 365 environments.
            </p>
            
            <div className="metadata-pentities-grid">
              <div className="metadata-pentities-card">
                <h3>Native SharePoint Features</h3>
                <p>
                  Microsoft 365 includes built-in metadata capabilities through SharePoint columns, content types, 
                  and term stores. These features provide basic metadata management without additional software.
                </p>
              </div>

              <div className="metadata-pentities-card">
                <h3>Managed Metadata Service</h3>
                <p>
                  SharePoint's Managed Metadata Service enables centralized taxonomy creation and management. Organizations 
                  can define hierarchical term sets for consistent metadata across site collections.
                </p>
              </div>

              <div className="metadata-pentities-card">
                <h3>Content Types</h3>
                <p>
                  Content types bundle metadata fields with document templates and workflow associations. This approach 
                  ensures documents of specific types carry appropriate metadata throughout their lifecycle.
                </p>
              </div>

              <div className="metadata-pentities-card">
                <h3>Third-Party Tools</h3>
                <p>
                  Various commercial solutions extend Microsoft 365 metadata capabilities. Tools like pEntities provide 
                  enhanced user interfaces, AI-powered suggestions, and cross-application metadata entry interfaces.
                </p>
              </div>

              <div className="metadata-pentities-card">
                <h3>Power Platform Integration</h3>
                <p>
                  Power Apps and Power Automate can create custom metadata management solutions. Organizations can build 
                  tailored interfaces and workflows that interact with SharePoint metadata programmatically.
                </p>
              </div>

              <div className="metadata-pentities-card">
                <h3>Microsoft Syntex</h3>
                <p>
                  Microsoft Syntex provides AI-powered content understanding that can automatically classify documents 
                  and extract metadata using machine learning models trained on organizational content.
                </p>
              </div>
            </div>


          </div>
        </section>

        {/* Use Cases Section */}
        <section className="metadata-usecases-section">
          <div className="metadata-container">
            <h2 className="metadata-section-title">Industry Use Cases</h2>
            <p className="metadata-section-subtitle">
              Metadata implementation patterns vary by industry and organizational requirements.
            </p>
            
            <div className="metadata-usecases-grid">
              <div className="metadata-usecase-card">
                <div className="metadata-usecase-number">01</div>
                <h3>Legal Services</h3>
                <p>
                  Legal organizations use metadata for matter management, client classification, and document type 
                  identification. This enables matter-based search, retention policy application, and conflict checking 
                  while maintaining confidentiality requirements.
                </p>
              </div>

              <div className="metadata-usecase-card">
                <div className="metadata-usecase-number">02</div>
                <h3>Project-Based Organizations</h3>
                <p>
                  Project-centric organizations classify documents by project code, phase, and status. This structure 
                  enables project-based views, automated approval workflows, and project health reporting through 
                  business intelligence tools.
                </p>
              </div>

              <div className="metadata-usecase-card">
                <div className="metadata-usecase-number">03</div>
                <h3>Sales Operations</h3>
                <p>
                  Sales departments classify content by customer, opportunity, and deal stage. Customer-based metadata 
                  enables rapid access to relationship history and supports automated workflows triggered by opportunity 
                  status changes.
                </p>
              </div>

              <div className="metadata-usecase-card">
                <div className="metadata-usecase-number">04</div>
                <h3>Human Resources</h3>
                <p>
                  HR departments classify employee documents using personnel identifiers and document categories. 
                  This supports retention compliance, privacy protection through metadata-driven security, and 
                  employee self-service portals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Summary Section */}
        <section className="metadata-cta-section">
          <div className="metadata-container">
            <div className="metadata-cta-content">
              <h2>Summary</h2>
              <p>
                Metadata provides the foundation for intelligent information management in Microsoft 365. Through structured 
                attributes applied to content, organizations enable precise discovery, automated workflows, compliance enforcement, 
                and business intelligence. Implementation approaches range from native SharePoint features to third-party solutions 
                and custom development using the Power Platform. When properly architected, metadata transforms document storage 
                into a structured information system aligned with organizational processes and governance requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="metadata-footer">
          <div className="metadata-container">
            <p>&copy; 2026 Meta-365. All rights reserved.</p>
          </div>
        </footer>
      </div>
    );
  }
}

export default Metadata;
